import React, { createContext, useContext, useState, useEffect } from 'react'
import { useFirestore } from '@hooks/useFirestore'

const ContentContext = createContext()

export const useContent = () => {
  const context = useContext(ContentContext)
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider')
  }
  return context
}

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({
    services: [],
    pages: {},
    settings: {}
  })
  const [loading, setLoading] = useState(true)

  const servicesFirestore = useFirestore('services')
  const pagesFirestore = useFirestore('pages')
  const settingsFirestore = useFirestore('settings')

  // Default page content
  const defaultPages = {
    home: {
      hero: {
        title: 'People. Purpose. Pay. Perfectly Aligned.',
        subtitle: 'We believe benchmarking breeds sameness, but strategy demands difference. Our comprehensive HR services framework rewires Total Rewards from the inside out – rooted in purpose, informed by real value creation, and delivered through high-impact talent. Pay becomes a strategic lever to drive performance, culture, and identity.'
      }
    },
    about: {
      hero: {
        title: 'Redefining Total Rewards Excellence',
        subtitle: 'We are Total Rewards Pro – a boutique consulting firm dedicated to transforming how organizations design, implement, and optimize their compensation and benefits strategies.'
      }
    },
    services: {
      hero: {
        title: 'Total Rewards Consulting Excellence',
        subtitle: 'Specialized total rewards consulting across 6 core areas: equity compensation, executive pay, variable pay design, benefits strategy, market benchmarking, and integrated rewards programs. Strategic compensation solutions that drive business results.'
      },
      process: {
        title: 'Our Total Rewards Methodology',
        subtitle: 'A comprehensive approach that aligns compensation, benefits, equity, and incentives with your business strategy',
        steps: [
          {
            step: '01',
            title: 'Total Rewards Assessment',
            description: 'Comprehensive analysis of your current compensation, benefits, equity programs, and total rewards strategy to identify optimization opportunities.'
          },
          {
            step: '02',
            title: 'Strategic Design & Alignment',
            description: 'Develop integrated total rewards solutions that align with your business strategy, culture, and talent objectives for maximum impact.'
          },
          {
            step: '03',
            title: 'Implementation & Optimization',
            description: 'Deploy total rewards programs with seamless integration, ongoing monitoring, and continuous optimization to ensure sustainable results.'
          }
        ]
      }
    },
    resources: {
      hero: {
        title: 'Insights & Resources',
        subtitle: 'Stay ahead of the curve with our comprehensive library of research, insights, and best practices in total rewards and compensation strategy.'
      }
    },
    contact: {
      hero: {
        title: 'Let\'s Transform Your Total Rewards Strategy',
        subtitle: 'Ready to create a compensation and benefits program that drives engagement, retention, and business success? We are here to help.'
      }
    },
    careers: {
      hero: {
        title: 'Join Our Team',
        subtitle: 'Build your career with Total Rewards Pro and help shape the future of compensation consulting.'
      }
    }
  }

  // Initialize default content
  const initializeDefaultContent = async () => {
    try {
      // Initialize pages if they don't exist
      if (pagesFirestore.data.length === 0) {
        console.log('Initializing default pages...')
        for (const [pageId, pageContent] of Object.entries(defaultPages)) {
          await pagesFirestore.addItem({ id: pageId, ...pageContent })
        }
      }

      // Initialize settings if they don't exist
      if (settingsFirestore.data.length === 0) {
        console.log('Initializing default settings...')
        await settingsFirestore.addItem({
          id: 'general',
          siteName: 'Total Rewards Pro',
          tagline: 'People. Purpose. Pay. Perfectly Aligned.',
          email: 'info@totalrewardspro.com',
          phone: '+91-9873655887'
        })
      }

    } catch (error) {
      console.error('Error initializing default content:', error)
    }
  }

  // Update content when Firestore data changes
  useEffect(() => {
    setContent(prev => ({
      ...prev,
      services: servicesFirestore.data
    }))
    
    // Check if we need to initialize default content
    if (!servicesFirestore.loading && !pagesFirestore.loading && !settingsFirestore.loading) {
      initializeDefaultContent()
      setLoading(false)
    }
  }, [servicesFirestore.data, servicesFirestore.loading])

  useEffect(() => {
    setContent(prev => ({
      ...prev,
      pages: pagesFirestore.data.reduce((acc, page) => {
        acc[page.id] = page
        return acc
      }, {})
    }))
  }, [pagesFirestore.data])

  useEffect(() => {
    setContent(prev => ({
      ...prev,
      settings: settingsFirestore.data.reduce((acc, setting) => {
        acc[setting.id] = setting
        return acc
      }, {})
    }))
  }, [settingsFirestore.data])

  const updateService = async (serviceId, data) => {
    return await servicesFirestore.updateItem(serviceId, data)
  }

  const updatePage = async (pageId, data) => {
    try {
      // Check if page exists
      const existingPage = pagesFirestore.data.find(page => page.id === pageId)
      
      if (existingPage) {
        // Update existing page
        return await pagesFirestore.updateItem(pageId, data)
      } else {
        // Create new page
        const newPageData = {
          id: pageId,
          ...data
        }
        return await pagesFirestore.addItem(newPageData)
      }
    } catch (error) {
      console.error('Error updating page:', error)
      throw error
    }
  }

  const updateSettings = async (settingId, data) => {
    return await settingsFirestore.updateItem(settingId, data)
  }

  const value = {
    content,
    loading: loading || servicesFirestore.loading || pagesFirestore.loading || settingsFirestore.loading,
    updateService,
    updatePage,
    updateSettings,
    servicesFirestore,
    pagesFirestore,
    settingsFirestore
  }

  return (
    <ContentContext.Provider value={value}>
      {children}
    </ContentContext.Provider>
  )
} 